import { useLingui } from "@lingui/react/macro";
import { createContext, ReactNode } from "react";
import { toast } from "sonner";
import { useSWRConfig } from "swr";
import useSWRSubscription from "swr/subscription";

import { mutateRelatedPokeList } from "~/hook/domain/useRelatedPokeList.ts";
import { SWR_KEY_MATE_POKE } from "~/hook/domain/useUserRelatedPokeList.ts";
import { PokeSheet } from "~/ui/overlay/PokeSheet.tsx";
import { useUser } from "~/ui/provider/Auth.tsx";
import { useStackedLayer } from "~/ui/provider/StackedLayerProvider.tsx";

const LivePokeStreamContext = createContext<null>(null);

interface StreamEvent {
  payload: {
    pokeId?: number;
    reaction?: string;
    relation?: {
      fromUser: UserCompact;
    };
  };
  type: "POKE" | "REACTION";
}

interface UserCompact {
  email: string;
  id: number;
  name: string;
}

export const LivePokeStreamProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { isLoggedIn, myInfo } = useUser();
  const { mutate: globalMutate } = useSWRConfig();
  const { t } = useLingui();
  const overlay = useStackedLayer();

  useSWRSubscription(
    isLoggedIn && myInfo?.token
      ? (["mate/poke/stream", myInfo.token] as const)
      : null,
    (key) => {
      const [, token] = key;
      const apiUrl = import.meta.env.VITE_YOGI_POKE_API_URL;
      const url = `${apiUrl}/mate/poke/stream?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);

      es.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data as string) as StreamEvent;
          if (parsed.type === "POKE") {
            const fromUser = parsed.payload.relation?.fromUser;
            if (!fromUser) return;

            void mutateRelatedPokeList();
            void globalMutate("user/my-info");
            void globalMutate(SWR_KEY_MATE_POKE(fromUser.email));

            if ("vibrate" in navigator) {
              navigator.vibrate([100, 50, 100]);
            }

            if (fromUser.id !== myInfo?.id) {
              const senderName = fromUser.name;
              const senderEmail = fromUser.email;
              toast(t`${senderName}님이 회원님을 콕 찔렀습니다!`, {
                action: {
                  label: t`답장하기`,
                  onClick: () => {
                    overlay(PokeSheet, { targetUserEmail: senderEmail });
                  },
                },
                description: `@${senderEmail}`,
                duration: 5000,
              });
            }
          } else if ((parsed.type as string) === "REACTION") {
            void mutateRelatedPokeList();
          }
        } catch (err) {
          console.error("Failed to parse SSE event data:", err);
        }
      };

      return () => {
        es.close();
      };
    },
  );

  return (
    <LivePokeStreamContext.Provider value={null}>
      {children}
    </LivePokeStreamContext.Provider>
  );
};
